import { Dispatch, FC, SetStateAction, useCallback, useMemo } from "react";
import {
  GridApi,
  RowClickedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { useWillUnmount } from "rooks";
import { useFlag } from "@unleash/proxy-client-react";
import { aiKubernetesRecommendationsSelector } from "@/store/ai-kubernetes-recommendations/selectors/aiKubernetesRecommendationsSelector";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { AgDataGrid } from "@/components/pages/common/ag-grid/AgDataGrid";
import {
  opportunitiesGridDefaultColumnDef,
  opportunitiesGridRowSelection,
  opportunitiesGridWrapperStyles,
} from "@/components/pages/common/opportunities/utils/constants/gridConstants";
import { taskAvailableStatusPropertiesSelector } from "@/store/task-management/selectors/properties/taskAvailableStatusPropertiesSelector";
import { getAiKubernetesRecommendationsTableColumnsSelector } from "@/components/pages/ai-kubernetes-recommendations/components/aiKubernetesRecommendationsTableColumns";
import { getAGGridAllSelectedRows } from "@/components/pages/common/ag-grid/utils/helpers/getSelectedRowsFromApi";
import { setSavingsOpportunitiesSelectedRowsIds } from "@/store/savings-opportunities/savingsOpportunitiesSlice";
import { KubernetesAiSavingsOpportunity } from "@/services/cloudchipr.api";
import { setAiKubernetesRecommendationsResourceDetailsDrawerResourceDataThunk } from "@/store/ai-kubernetes-recommendations/thunks/setAiKubernetesRecommendationsResourceDetailsDrawerResourceDataThunk";

interface AiKubernetesRecommendationsGrid {
  gridApi: GridApi | null;
  setGridApi: Dispatch<SetStateAction<GridApi | null>>;
}

export const AiKubernetesRecommendationsGrid: FC<
  AiKubernetesRecommendationsGrid
> = ({ gridApi, setGridApi }) => {
  const dispatch = useAppDispatch();
  const data = useAppSelector(aiKubernetesRecommendationsSelector);
  const statuses = useAppSelector(taskAvailableStatusPropertiesSelector);

  const enableTaskCreationFromAiKubernetesRecommendations = useFlag(
    "EnableTaskCreationFromAiKubernetesRecommendations",
  );
  const enableKubernetesAiRecommendationsDrawer = useFlag(
    "EnableKubernetesAiRecommendationsDrawer",
  );
  const enableBillingCostAndForecastedEOMForOpportunities = useFlag(
    "EnableBillingCostAndForecastedEOMForOpportunities",
  );

  const columnDefs = useMemo(
    () =>
      getAiKubernetesRecommendationsTableColumnsSelector(
        statuses,
        enableBillingCostAndForecastedEOMForOpportunities,
      ),
    [statuses, enableBillingCostAndForecastedEOMForOpportunities],
  );

  const defaultColDef = useMemo(
    () => ({
      ...opportunitiesGridDefaultColumnDef,
      filter: "agMultiColumnFilter",
      cellStyle: enableKubernetesAiRecommendationsDrawer
        ? opportunitiesGridDefaultColumnDef.cellStyle
        : { cursor: "default" },
    }),
    [enableKubernetesAiRecommendationsDrawer],
  );

  const rowSelectHandler = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedRows = getAGGridAllSelectedRows(api);

      dispatch(
        setSavingsOpportunitiesSelectedRowsIds(
          selectedRows.map((row) => row.id),
        ),
      );
    },
    [dispatch],
  );

  const rowClickHandler = useCallback(
    (e: RowClickedEvent<KubernetesAiSavingsOpportunity>) => {
      const data = e.data;

      if (!data?.resource_id || !enableKubernetesAiRecommendationsDrawer) {
        return;
      }

      gridApi?.deselectAll();

      dispatch(
        setAiKubernetesRecommendationsResourceDetailsDrawerResourceDataThunk({
          opportunity: data,
          enableBillingCostAndForecastedEOMForOpportunities,
        }),
      );
    },
    [
      gridApi,
      dispatch,
      enableBillingCostAndForecastedEOMForOpportunities,
      enableKubernetesAiRecommendationsDrawer,
    ],
  );

  useWillUnmount(() => {
    dispatch(setSavingsOpportunitiesSelectedRowsIds([]));
  });

  return (
    <AgDataGrid
      rowData={data}
      rowHeight={55}
      columnDefs={columnDefs}
      gridApiSetter={setGridApi}
      defaultColDef={defaultColDef}
      onRowClicked={rowClickHandler}
      onSelectionChanged={rowSelectHandler}
      rowSelection={
        enableTaskCreationFromAiKubernetesRecommendations
          ? opportunitiesGridRowSelection
          : undefined
      }
      wrapperStyles={opportunitiesGridWrapperStyles}
      configToRememberStateInStorage={configToRememberStateInStorage}
    />
  );
};

const configToRememberStateInStorage = {
  uniqueNameAsKey: "ai-kubernetes-recommendations",
};
